// apps/landing-page/src/components/analytics/PageViewTracker.tsx
'use client';

import { Suspense, useEffect, useRef, type MutableRefObject } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { trackPageView } from '@/lib/analytics';
import { captureFirstTouch } from '@/lib/first-touch';
import { CONSENT_CHANGE_EVENT, hasAnalyticsConsent, type ConsentChangeDetail } from '@/lib/consent';

/**
 * Emits `page.viewed` once per distinct route (pathname + query string).
 * Mounted once near the root layout — NOT this app's unrelated `<Analytics />`
 * (Google Analytics 4 script loader), a different, pre-existing concern.
 *
 * `useSearchParams()` opts its subtree into client-side rendering, so this
 * wraps its own `Suspense` boundary rather than depending on whatever the
 * surrounding layout happens to provide.
 */
export function PageViewTracker() {
  return (
    <Suspense fallback={null}>
      <PageViewTrackerInner />
    </Suspense>
  );
}

function PageViewTrackerInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Guards against StrictMode's dev-only double effect invocation firing the
  // same route twice — see useProductViewTracking's doc comment for why a
  // ref (not a cleanup fn) is the right guard here. It is only set once a view
  // has actually been recorded (i.e. with consent), so a route seen before the
  // visitor accepted is still recorded when they accept.
  const lastTracked = useRef<string | null>(null);
  const search = searchParams.toString();

  useEffect(() => {
    recordView(lastTracked, pathname, search);
  }, [pathname, search]);

  // Opt-in: the first consented view is the one the visitor is on when they press
  // "Setuju", so record it right away instead of waiting for the next navigation.
  useEffect(() => {
    const onConsentChange = (e: Event) => {
      if ((e as CustomEvent<ConsentChangeDetail>).detail !== 'granted') return;
      recordView(lastTracked, pathname, search);
    };
    window.addEventListener(CONSENT_CHANGE_EVENT, onConsentChange);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onConsentChange);
  }, [pathname, search]);

  return null;
}

function recordView(lastTracked: MutableRefObject<string | null>, pathname: string, search: string): void {
  const key = search ? `${pathname}?${search}` : pathname;
  if (lastTracked.current === key) return;
  // No consent: record nothing and remember nothing (see the ref's comment above).
  if (!hasAnalyticsConsent()) return;
  lastTracked.current = key;
  // Consent-gated and write-once, so calling it on every distinct view is safe.
  captureFirstTouch(pathname, search || undefined);
  trackPageView(pathname, search || undefined);
}
